import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { Formik, Field, FieldProps, Form } from 'formik';
import { Box, Stack, VStack, Text } from '@chakra-ui/layout';
import { InputGroup, Input, FormControl, InputLeftElement, InputRightElement, Tooltip, Button, Icon } from '@chakra-ui/react';
import { AiOutlineEye } from '@react-icons/all-files/ai/AiOutlineEye';
import { AiOutlineEyeInvisible } from '@react-icons/all-files/ai/AiOutlineEyeInvisible';
import { loginValidation } from '.';
import { LoginFormFields, LoginSchema } from '../types';
import { EnvelopeIcon } from 'lib/icons/envelope';
import { PadlockIcon } from 'lib/icons/padlock';
import { Identities } from 'domains/login';
import { EnterAnimation, FormAlert, ButtonSubmit, DividerWithText } from 'components';
import { loginClient, userClient, teamInvitesClient } from "lib/clients";
import { teamAppUrl } from "lib/utils";
import { useE2EEContext, sha512, generateKeys } from 'lib/e2ee';
import { useLastVisitedShare } from 'lib/hooks';

type OnSubmit = (values: LoginSchema) => void;

type State = {
  error: string;
  success: string;
  showPassword: boolean;
  redirectTo?: string;
}

export const LoginForm: FC = () => {
  const [state, setState] = useState<State>({
    error: '',
    success: '',
    showPassword: false,
    redirectTo: null,
  });
  const { push, query: { next }} = useRouter();
  const { join } = useLastVisitedShare();

  const toggleShowPassword = (): void => setState({
    ...state,
    showPassword: !state.showPassword,
  });

  const redirect = async (user: any): Promise<void> => {
    const { data: teams } = await userClient.getTeams();
    const { data: invites } = await teamInvitesClient.getInvitesByEmail(user.email);

    // If they have multiple teams, let them pick
    // If they have any invites, show them
    // If they have no invites AND no teams, send them to the teams page so they can create one.
    if (teams.length > 1 || invites.length > 0 || (invites.length === 0 && teams.length === 0)) {
      await push(`/teams?next=${next || '/packs'}`); // No need to display animation, that will happen after the teams page.
      return;
    }

    await join(teams[0].identifier, user.public_key_armored);

    // If the user has just the one team take them straight there
    const teamUrl = teamAppUrl(teams[0].identifier);

    setState({ ...state, redirectTo: teamUrl + (next || '/packs')}); // Going to the packs page, so display animation.
    return;
  }

  const onSubmit: OnSubmit = async ({ email, password }): Promise<void> => {
    setState({ ...state, error: '', success: '' });

    const hash = sha512(password);
    let user = null;

    try {
      try {
        await loginClient.postLogin(email, hash);
        const { data } = await userClient.getUser();

        user = data;
      }
      catch(e) {
        if (e?.response?.status !== 403) {
          throw e;
        }

        // This is an older user, login using just the password. If that is successful, then update the user with the hash value
        await loginClient.postLogin(email, password);

        const { data } = await userClient.getUser();

        const userId = { email: data.email, name: `${data.first_name} ${data.last_name}`};
        const { privateKeyArmored, publicKeyArmored } = await generateKeys(userId, password);

        const { data: updatedUser } = await userClient.update({password: hash, private_key_armored: privateKeyArmored, public_key_armored: publicKeyArmored});

        user = updatedUser;
      }

      setState({
        ...state,
        error: '',
        success: 'Logged in successfully',
      });

      await redirect(user);
    } catch (err) {
      console.log(err)
      let error = 'There was an error, please try again';

      if (err?.response?.status < 500) {
        error = 'Please check your credentials and try again'
      }

      setState({ ...state, error, success: '' });
    }
  }

  if (state.redirectTo) {
    return <EnterAnimation onComplete={() => window.location.replace(state.redirectTo)} />;
  }

  return (
    <>
      <Identities
        onSuccess={(user: any) => {
          setState({
            ...state,
            error: '',
            success: 'Login successful'
          })
          redirect(user);
        }}
      />
      <Formik
        onSubmit={onSubmit}
        validationSchema={loginValidation}
        initialValues={{ email: '', password: '' }}
      >
        <Form style={{ width: '100%' }}>
          <Stack spacing={6} width="100%">
            <FormAlert error={state.error} success={state.success} />
            <Field key="email" name={LoginFormFields.email}>
              {({ field, form, meta }: FieldProps) => (
                <FormControl
                  isInvalid={Boolean(meta.touched && form.errors[field.name])}
                  isRequired={true}
                >
                  <InputGroup size="md">
                    <InputLeftElement height="100%" width="30px" ml="15px">
                      <EnvelopeIcon />
                    </InputLeftElement>
                    <Input
                      pr="2rem"
                      pl="4rem"
                      size="lg"
                      type="email"
                      isRequired={true}
                      placeholder="Email address"
                      {...field}
                    />
                  </InputGroup>
                </FormControl>
              )}
            </Field>
            <Field key="password" name={LoginFormFields.password}>
              {({ field, meta, form }: FieldProps) => (
                <FormControl
                  isInvalid={Boolean(meta.touched && form.errors[field.name])}
                  isRequired={true}
                >
                  <InputGroup size="md">
                    <InputLeftElement height="100%" width="30px" ml="15px">
                      <PadlockIcon />
                    </InputLeftElement>
                    <Input
                      pr="2rem"
                      pl="4rem"
                      size="lg"
                      placeholder='Password'
                      isRequired={true}
                      type={state.showPassword ? "text" : "password"}
                      {...field}
                    />
                    <InputRightElement mr="15px" height="100%">
                      {state.showPassword ? (
                        <Tooltip label="Hide password">
                          <Button onClick={toggleShowPassword}>
                            <Icon boxSize="24px" as={AiOutlineEye}/>
                          </Button>
                        </Tooltip>
                        ) : (
                        <Tooltip label="Show password">
                          <Button colorScheme="brand" onClick={toggleShowPassword}>
                            <Icon boxSize="24px" as={AiOutlineEyeInvisible} />
                          </Button>
                        </Tooltip>
                      )}
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
              )}
            </Field>
            <Box width="100%" textAlign="right">
              <Text variant={"secondary"} size={"sm"}>
                <Link href="/forgot-password" passHref>
                  <a>Forgot Password?</a>
                </Link>
              </Text>
            </Box>
            <VStack spacing={5} width="100%">
              <ButtonSubmit
                name="login"
                size="xl"
                isFullWidth={true}
              >
                Log in
              </ButtonSubmit>
              <Box>
                <Text size="sm" color="text.200">
                  This site is protected by reCAPTCHA and the Google <a rel="noreferrer" href="https://policies.google.com/privacy" target="_blank">Privacy Policy</a> and <a rel="noreferrer" href="https://policies.google.com/terms" target="_blank">Terms of Service</a> apply.
                </Text>
              </Box>
              <DividerWithText>or</DividerWithText>
              <Button
                isDisabled={!!state.success.length}
                size="xl"
                isFullWidth={true}
                variant="secondary"
                onClick={() => push('/register')}
              >
                Sign up / Join a team
              </Button>
            </VStack> 
          </Stack>
        </Form>
      </Formik>
    </>
  )
}
